import React, { useRef, useEffect } from 'react';
import User from 'lucide-react/dist/esm/icons/user';
import Send from 'lucide-react/dist/esm/icons/send';
import X from 'lucide-react/dist/esm/icons/x';
import Copy from 'lucide-react/dist/esm/icons/copy';
import Download from 'lucide-react/dist/esm/icons/download';
import Check from 'lucide-react/dist/esm/icons/check';
import { ChatMessageSkeleton } from '../../../components/common/LoadingSkeleton';
import { useChatStore } from '../../../stores';
import { getTranslation } from '../../../i18n';
import { parseMarkdown, formatMessageTime } from '../../../utils/markdown';
import type { SupportedLanguage } from '../../../types';

interface ChatPanelProps {
  analysisId: string | null;
  language: SupportedLanguage;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ analysisId, language }) => {
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const t = (key: string) => getTranslation(language, key);

  const {
    chatOpen,
    chatMessages,
    chatInput,
    chatLoading,
    chatHistoryLoading,
    copiedMessageIndex,
    exportingChat,
    setChatOpen,
    toggleChat,
    setChatInput,
    sendMessage,
    copyMessage,
    exportChat,
  } = useChatStore();

  // Auto-scroll on new messages
  useEffect(() => {
    if (chatMessagesRef.current && chatOpen) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [chatMessages, chatLoading, chatOpen]);

  if (!chatOpen) return null;

  return (
    <div 
      className="fixed bottom-16 right-6 w-[360px] bg-surface-page border border-default rounded-xl shadow-[0_10px_40px_rgba(0,0,0,0.5)] z-sticky overflow-hidden transition-all duration-300"
      data-walkthrough="chat"
    >
      {/* Chat Header */}
      <div 
        className="flex items-center justify-between py-3 px-3.5 bg-gradient-to-r from-status-success/10 to-transparent border-b border-subtle cursor-pointer transition-colors duration-200 hover:from-status-success/15"
        onClick={toggleChat}
      >
        <div className="flex items-center gap-2.5 flex-1">
          <img src="/quazi-logo.svg" alt="QUAZI" className="w-5 h-5 rounded-md flex-shrink-0" />
          <div className="flex items-center gap-2 flex-1">
            <span className="text-xs font-semibold text-content tracking-wide">{t('chatWithQuazi')}</span>
            {chatMessages.length > 0 && (
              <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-gradient-to-br from-brand to-brand-hover rounded-full text-[10px] font-bold text-content-inverse flex-shrink-0">
                {chatMessages.length}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
          {chatMessages.length > 0 && (
            <button 
              className="inline-flex items-center justify-center w-6 h-6 bg-surface-primary border border-default rounded-md text-content-secondary cursor-pointer transition-all duration-300 hover:bg-surface-elevated hover:text-content disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={() => analysisId && exportChat(analysisId)}
              disabled={exportingChat}
              title="Export conversation"
            >
              <Download size={12} />
            </button>
          )}
          <button 
            className="flex items-center justify-center w-6 h-6 bg-surface-primary border border-default rounded-md text-content-secondary cursor-pointer p-0 transition-all duration-200 hover:bg-surface-elevated hover:text-content"
            onClick={(e) => {
              e.stopPropagation();
              setChatOpen(false);
            }}
            title={t('closeChat')}
          >
            <X size={12} />
          </button>
        </div>
      </div>
      
      {/* Chat Body */}
      <div className="flex flex-col h-[420px] animate-[chatSlideIn_0.25s_ease-out]">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-3 flex flex-col gap-2.5 scroll-smooth" ref={chatMessagesRef}>
          {chatHistoryLoading ? (
            <div className="p-3">
              <ChatMessageSkeleton />
              <ChatMessageSkeleton />
            </div>
          ) : chatMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center text-content-muted gap-4">
              <img src="/quazi-logo.svg" alt="QUAZI" className="w-8 opacity-30 mb-2" />
              <p className="m-0 text-xs">{t('chatEmptyState')}</p>
              <div className="flex flex-col gap-2 w-full max-w-[260px]">
                {[
                  { en: 'What are the main financial risks?', es: '¿Cuáles son los principales riesgos financieros?', pt: 'Quais são os principais riscos financeiros?', short: { en: 'Main risks?', es: '¿Principales riesgos?', pt: 'Principais riscos?' } },
                  { en: 'Explain the low gross margin', es: 'Explica el margen bruto bajo', pt: 'Explique a margem bruta baixa', short: { en: 'Explain margins', es: 'Explicar márgenes', pt: 'Explicar margens' } },
                  { en: 'What actions should be taken first?', es: '¿Qué acciones recomiendas tomar primero?', pt: 'Quais ações você recomenda tomar primeiro?', short: { en: 'What to do first?', es: '¿Qué hacer primero?', pt: 'O que fazer primeiro?' } }
                ].map((q, idx) => (
                  <button 
                    key={idx}
                    className="py-2.5 px-3 bg-status-success/10 border border-status-success/30 rounded-lg text-status-success text-xs font-medium cursor-pointer text-left whitespace-nowrap overflow-hidden text-ellipsis transition-all duration-200 hover:bg-status-success/15 hover:border-status-success/50 hover:translate-x-0.5"
                    onClick={() => {
                      const question = language === 'es' ? q.es : language === 'pt' ? q.pt : q.en;
                      setChatInput(question);
                      if (analysisId) sendMessage(analysisId, question);
                    }}
                  >
                    {language === 'es' ? q.short.es : language === 'pt' ? q.short.pt : q.short.en}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            chatMessages.map((msg, idx) => (
              <div 
                key={idx} 
                className={`flex gap-2.5 mb-3.5 animate-[messageSlideIn_0.25s_ease-out] ${msg.role === 'user' ? 'flex-row-reverse' : ''} group`}
              >
                <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center mt-0.5">
                  {msg.role === 'assistant' ? (
                    <img src="/quazi-logo.svg" alt="QUAZI AI" className="w-7 h-7 rounded-full p-1 bg-status-success/10 border border-status-success/30" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-status-success to-emerald-600 flex items-center justify-center text-content-inverse">
                      <User size={16} />
                    </div>
                  )}
                </div>
                <div className={`relative flex-1 max-w-[calc(100%-38px)] ${msg.role === 'user' ? 'flex flex-col items-end' : ''}`}>
                  <div 
                    className={`m-0 py-2.5 px-3.5 rounded-xl text-[0.8125rem] leading-relaxed break-words ${
                      msg.role === 'user' 
                        ? 'bg-gradient-to-br from-status-success to-emerald-600 text-content-inverse rounded-br-sm inline-block max-w-full' 
                        : `bg-surface-primary text-content border border-subtle rounded-bl-sm ${msg.isError ? 'bg-status-error/10 border-status-error/40' : ''}`
                    }`}
                    dangerouslySetInnerHTML={{ __html: parseMarkdown(msg.content) }} 
                  />
                  {msg.timestamp && (
                    <span className={`text-[10px] text-content-muted mt-1 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                      {formatMessageTime(msg.timestamp)}
                    </span>
                  )}
                  <button 
                    className="absolute top-2 right-2 hidden group-hover:inline-flex items-center justify-center w-7 h-7 bg-brand/20 border border-brand/30 rounded-md text-primary cursor-pointer z-10 transition-all duration-200 hover:bg-brand/30 hover:border-brand/50 hover:scale-110 active:scale-95"
                    onClick={() => copyMessage(msg, idx, analysisId)}
                    title="Copy message"
                  >
                    {copiedMessageIndex === idx ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                </div>
              </div>
            ))
          )}
          {chatLoading && (
            <div className="flex gap-2.5 mb-3.5 animate-[messageSlideIn_0.25s_ease-out]">
              <div className="flex-shrink-0 w-7 h-7 flex items-center justify-center mt-0.5">
                <img src="/quazi-logo.svg" alt="QUAZI AI" className="w-7 h-7 rounded-full p-1 bg-status-success/10 border border-status-success/30 animate-pulse-glow" />
              </div>
              <div className="flex-1">
                <div className="flex flex-col gap-1.5 py-2.5 px-3.5 bg-surface-primary border border-subtle rounded-xl rounded-bl-sm w-fit">
                  <span className="text-[11px] text-status-success font-medium">QUAZI is thinking...</span>
                  <div className="flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-gradient-to-br from-status-success to-cyan-500 rounded-full animate-[typingBounce_1.4s_infinite_ease-in-out]" style={{ animationDelay: '0s' }} />
                    <span className="w-1.5 h-1.5 bg-gradient-to-br from-status-success to-cyan-500 rounded-full animate-[typingBounce_1.4s_infinite_ease-in-out]" style={{ animationDelay: '0.2s' }} />
                    <span className="w-1.5 h-1.5 bg-gradient-to-br from-status-success to-cyan-500 rounded-full animate-[typingBounce_1.4s_infinite_ease-in-out]" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Input */}
        <div className="flex gap-2 py-3.5 px-4 border-t border-subtle bg-black/20 relative">
          <span className="absolute inset-[-2px] bg-gradient-to-br from-status-success via-cyan-500 via-brand to-status-success bg-[length:300%_300%] rounded-xl opacity-0 -z-10 blur-[4px] transition-opacity duration-300 focus-within:opacity-60 animate-[gradientGlow_3s_ease_infinite]" />
          <input
            type="text"
            placeholder={t('askQuestion')}
            value={typeof chatInput === 'string' ? chatInput : ''}
            onChange={(e) => setChatInput(e.target.value || '')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (analysisId) sendMessage(analysisId);
              }
            }}
            disabled={chatLoading}
            className="flex-1 py-2.5 px-3.5 bg-surface-primary border border-default rounded-xl text-content text-[0.8125rem] font-sans transition-all duration-200 focus:outline-none focus:border-status-success/50 focus:bg-surface-secondary placeholder:text-content-muted disabled:opacity-50"
          />
          <button
            onClick={() => analysisId && sendMessage(analysisId)}
            disabled={chatLoading || !(typeof chatInput === 'string' && chatInput.trim())}
            className="flex items-center justify-center w-9 h-9 bg-gradient-to-br from-status-success to-emerald-600 border-none rounded-xl text-content-inverse cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-[0_4px_12px_rgba(16,185,129,0.4)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(ChatPanel);
